'use client';

/**
 * Review Stage View
 * Preview panel with accept/revise actions
 * @see Issue #232: Create ReviewPage with preview panel
 * @see Issue #233: Implement preview embed/iframe
 * @see Issue #234: Implement Accept action
 * @see Issue #235: Implement Request Revision action
 * @see Issue #236: Implement revision cycle counter and limit
 */

import type { Request } from '@cortex/database';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Center,
  Heading,
  Inline,
  Label,
  Spinner,
  Stack,
  Text,
  TextArea,
} from '@cortex/ui';
import {
  AlertTriangle,
  Ban,
  CheckCircle,
  ExternalLink,
  Eye,
  RefreshCcw,
  RotateCcw,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';

import type { PreviewStatus } from './types';
import { MAX_REVISIONS } from './types';

/**
 * Format milliseconds to human readable duration
 */
function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds > 0
    ? `${minutes}m ${remainingSeconds}s`
    : `${minutes}m`;
}

interface ReviewStageViewProps {
  request: Request;
  onRequestUpdate: (request: Request) => void;
}

export default function ReviewStageView({
  request,
  onRequestUpdate,
}: ReviewStageViewProps) {
  const fetchWithAuth = useFetchWithAuth();

  // Preview state
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus | null>(
    null
  );
  const [isLoadingPreview, setIsLoadingPreview] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  // Use ref to track current status for polling without causing effect re-runs
  const previewStatusRef = useRef<PreviewStatus | null>(null);
  useEffect(() => {
    previewStatusRef.current = previewStatus;
  }, [previewStatus]);

  // Revision state
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [isSubmittingRevision, setIsSubmittingRevision] = useState(false);

  // Accept state
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Get current revision count from request
  const revisionCount =
    (request as Request & { revision_count?: number }).revision_count ?? 0;
  const isLastRevision = revisionCount >= MAX_REVISIONS - 1;
  const isRevisionLimitReached = revisionCount >= MAX_REVISIONS;

  // Fetch preview status on mount and poll for updates
  useEffect(() => {
    let isMounted = true;
    let pollInterval: ReturnType<typeof setInterval> | null = null;

    async function fetchPreviewStatus() {
      try {
        const response = await fetchWithAuth(
          `/requests/${request.id}/preview/status`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch preview status');
        }

        const result = await response.json();

        if (!isMounted) return;

        // API returns { data: PreviewStatus }, extract the inner data
        setPreviewStatus(result.data);
        setPreviewError(null);

        // Stop polling if preview is in terminal state
        if (
          result.data.status === 'ready' ||
          result.data.status === 'failed' ||
          result.data.status === 'cancelled'
        ) {
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
        }
      } catch (err) {
        if (!isMounted) return;
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load preview';
        setPreviewError(errorMessage);
      } finally {
        if (isMounted) {
          setIsLoadingPreview(false);
        }
      }
    }

    void fetchPreviewStatus();

    // Poll every 5 seconds if status is pending or building
    // Use ref to check current status to avoid re-creating interval on each status change
    pollInterval = setInterval(() => {
      const currentStatus = previewStatusRef.current?.status;
      if (currentStatus === 'pending' || currentStatus === 'building') {
        void fetchPreviewStatus();
      }
    }, 5000);

    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [request.id, fetchWithAuth]);

  // Handle accepting the changes
  const handleAccept = useCallback(async () => {
    setIsAccepting(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/transition`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'accept_changes',
            payload: {
              type: 'accept_changes',
              previewUrl: previewStatus?.previewUrl,
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to accept changes');
      }

      const data = await response.json();
      onRequestUpdate(data.request);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to accept changes';
      setError(errorMessage);
    } finally {
      setIsAccepting(false);
      setShowAcceptConfirm(false);
    }
  }, [request.id, previewStatus?.previewUrl, onRequestUpdate, fetchWithAuth]);

  // Handle requesting a revision
  const handleRequestRevision = useCallback(async () => {
    if (!revisionFeedback.trim()) {
      setError('Please provide feedback for the revision request');
      return;
    }

    setIsSubmittingRevision(true);
    setError(null);

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/transition`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'request_revision',
            payload: {
              type: 'request_revision',
              feedback: revisionFeedback.trim(),
            },
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.message || 'Failed to request revision'
        );
      }

      const data = await response.json();
      onRequestUpdate(data.request);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to request revision';
      setError(errorMessage);
    } finally {
      setIsSubmittingRevision(false);
    }
  }, [request.id, revisionFeedback, onRequestUpdate, fetchWithAuth]);

  // Handle refresh preview
  const handleRefreshPreview = useCallback(async () => {
    setIsLoadingPreview(true);

    try {
      const response = await fetchWithAuth(
        `/requests/${request.id}/preview/status`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch preview status');
      }

      const result = await response.json();
      // API returns { data: PreviewStatus }, extract the inner data
      setPreviewStatus(result.data);
      setPreviewError(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to refresh preview';
      setPreviewError(errorMessage);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [request.id, fetchWithAuth]);

  // Render revision form
  if (showRevisionForm && !isRevisionLimitReached) {
    return (
      <Stack gap="lg">
        {/* Revision Form Header */}
        <Card className="p-6">
          <Inline justify="between" align="center">
            <Inline gap="sm" align="center">
              <RotateCcw className="text-warning h-6 w-6" />
              <Stack gap="xs">
                <Heading level={2} className="text-lg font-semibold">
                  Request Revision
                </Heading>
                <Text color="muted" size="sm">
                  Revision {revisionCount + 1} of {MAX_REVISIONS}
                </Text>
              </Stack>
            </Inline>
            <Button
              variant="outline"
              onClick={() => setShowRevisionForm(false)}
            >
              Cancel
            </Button>
          </Inline>

          {/* Warning for last revision */}
          {isLastRevision && (
            <Alert variant="warning" className="mt-4">
              <AlertTriangle className="h-5 w-5" />
              <AlertDescription>
                This is your last available revision. After this, you must
                accept the changes.
              </AlertDescription>
            </Alert>
          )}

          {/* Feedback Input */}
          <Stack gap="xs" className="mt-4">
            <Label htmlFor="feedback">What changes would you like?</Label>
            <Text color="muted" size="sm" className="mt-1">
              Be specific about what needs to be changed. This will guide the
              agent in the next development cycle.
            </Text>
            <TextArea
              id="feedback"
              value={revisionFeedback}
              onChange={(e) => setRevisionFeedback(e.target.value)}
              placeholder="Describe the changes you want..."
              rows={5}
              className="mt-2"
            />
          </Stack>

          {/* Error message */}
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Submit Button */}
          <Inline justify="end" className="mt-4">
            <Button
              onClick={() => void handleRequestRevision()}
              disabled={!revisionFeedback.trim()}
              loading={isSubmittingRevision}
              leftAddon={
                !isSubmittingRevision ? (
                  <RotateCcw className="h-4 w-4" />
                ) : undefined
              }
            >
              {isSubmittingRevision
                ? 'Submitting...'
                : 'Submit Revision Request'}
            </Button>
          </Inline>
        </Card>
      </Stack>
    );
  }

  // Render accept confirmation
  if (showAcceptConfirm) {
    return (
      <Stack gap="lg">
        <Alert variant="success" className="p-6">
          <Inline gap="sm" align="center">
            <CheckCircle className="text-success h-6 w-6" />
            <Heading level={2} className="text-lg font-semibold">
              Accept Changes
            </Heading>
          </Inline>

          <Text color="secondary" className="mt-4">
            Are you sure you want to accept these changes? This will proceed to
            finalize the request and create the pull request.
          </Text>

          {/* Error message */}
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Action buttons */}
          <Inline justify="end" gap="sm" className="mt-6">
            <Button
              variant="outline"
              onClick={() => setShowAcceptConfirm(false)}
              disabled={isAccepting}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handleAccept()}
              loading={isAccepting}
              leftAddon={
                !isAccepting ? <CheckCircle className="h-4 w-4" /> : undefined
              }
            >
              {isAccepting ? 'Accepting...' : 'Yes, Accept Changes'}
            </Button>
          </Inline>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header with revision counter */}
      <Card className="p-6">
        <Inline justify="between" align="center">
          <Inline gap="sm" align="center">
            <Eye className="text-info h-6 w-6" />
            <Stack gap="xs">
              <Heading level={2} className="text-lg font-semibold">
                Ready for Review
              </Heading>
              <Text color="muted" size="sm">
                Review the preview and approve or request changes
              </Text>
            </Stack>
          </Inline>

          {/* Revision counter */}
          <Stack gap="none" align="end">
            <Text color="muted" size="sm">
              Revisions used
            </Text>
            <Text
              size="lg"
              weight="semibold"
              color={
                isRevisionLimitReached
                  ? 'error'
                  : isLastRevision
                    ? 'warning'
                    : 'default'
              }
            >
              {revisionCount} / {MAX_REVISIONS}
            </Text>
          </Stack>
        </Inline>
      </Card>

      {/* Preview Panel */}
      <Card className="p-6">
        <Inline justify="between" align="center" className="mb-4">
          <Inline gap="sm" align="center">
            <Heading level={3} className="font-semibold">
              Preview
            </Heading>
          </Inline>
          <Inline gap="sm" align="center">
            {previewStatus?.previewUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(previewStatus.previewUrl!, '_blank')}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open in New Tab
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => void handleRefreshPreview()}
              disabled={isLoadingPreview}
            >
              <RefreshCcw
                className={`h-4 w-4 ${isLoadingPreview ? 'animate-spin' : ''}`}
              />
            </Button>
          </Inline>
        </Inline>

        {/* Preview loading state */}
        {isLoadingPreview && !previewStatus?.previewUrl && (
          <Card className="bg-surface-2">
            <CardContent className="p-6">
              <Center className="h-[500px]">
                <Stack gap="md" align="center">
                  <Spinner size="md" className="mx-auto" />
                  <Text color="muted">Loading preview...</Text>
                </Stack>
              </Center>
            </CardContent>
          </Card>
        )}

        {/* Preview error state */}
        {previewError && (
          <Alert variant="destructive">
            <Center className="h-[500px]">
              <Stack gap="md" align="center">
                <AlertTriangle className="text-error h-8 w-8" />
                <Text color="error">{previewError}</Text>
                <Button
                  variant="outline"
                  onClick={() => void handleRefreshPreview()}
                >
                  Try Again
                </Button>
              </Stack>
            </Center>
          </Alert>
        )}

        {/* Preview pending/building state */}
        {!previewError &&
          previewStatus &&
          (previewStatus.status === 'pending' ||
            previewStatus.status === 'building') && (
            <Card className="bg-surface-2">
              <CardContent className="p-6">
                <Center className="h-[500px]">
                  <Stack gap="md" align="center">
                    <Spinner size="md" className="mx-auto" />
                    <Text>
                      {previewStatus.status === 'building'
                        ? 'Building preview...'
                        : 'Preparing preview...'}
                    </Text>
                    <Text color="muted" size="sm">
                      This may take a few minutes
                    </Text>
                  </Stack>
                </Center>
              </CardContent>
            </Card>
          )}

        {/* Preview not started state */}
        {!previewError &&
          previewStatus &&
          previewStatus.status === 'not_started' && (
            <Card className="bg-surface-2">
              <CardContent className="p-6">
                <Center className="h-[500px]">
                  <Stack gap="md" align="center">
                    <Eye className="text-foreground/60 h-12 w-12" />
                    <Text color="muted">Preview not available</Text>
                    <Text color="muted" size="sm">
                      The development process must complete before preview is
                      available
                    </Text>
                  </Stack>
                </Center>
              </CardContent>
            </Card>
          )}

        {/* Preview failed state */}
        {!previewError &&
          previewStatus &&
          previewStatus.status === 'failed' && (
            <Alert variant="destructive">
              <Center className="h-[500px]">
                <Stack gap="md" align="center" className="max-w-md">
                  <AlertTriangle className="text-error h-8 w-8" />
                  <Text color="error" weight="medium">
                    Preview deployment failed
                  </Text>
                  {previewStatus.error && (
                    <>
                      <Text color="muted" size="sm">
                        {previewStatus.error.message}
                      </Text>
                      {previewStatus.error.suggestion && (
                        <Text color="muted" size="xs">
                          {previewStatus.error.suggestion}
                        </Text>
                      )}
                    </>
                  )}
                </Stack>
              </Center>
            </Alert>
          )}

        {/* Preview cancelled state */}
        {!previewError &&
          previewStatus &&
          previewStatus.status === 'cancelled' && (
            <Card className="bg-surface-2">
              <CardContent className="p-6">
                <Center className="h-[500px]">
                  <Stack gap="md" align="center">
                    <Ban className="text-foreground/60 h-8 w-8" />
                    <Text color="muted">Preview deployment was cancelled</Text>
                    <Button
                      variant="outline"
                      onClick={() => void handleRefreshPreview()}
                    >
                      <RefreshCcw className="mr-2 h-4 w-4" />
                      Refresh Status
                    </Button>
                  </Stack>
                </Center>
              </CardContent>
            </Card>
          )}

        {/* Preview ready - show card with link to open in new tab */}
        {!previewError &&
          previewStatus?.previewUrl &&
          previewStatus.status === 'ready' && (
            <Center className="border-success/30 from-success/5 h-[400px] rounded-lg border bg-gradient-to-b to-transparent">
              <Stack gap="md" align="center">
                <Center className="bg-success/10 mb-4 h-16 w-16 rounded-full">
                  <CheckCircle className="text-success h-8 w-8" />
                </Center>
                <Heading level={3} className="text-xl font-semibold">
                  Preview Ready
                </Heading>
                <Text color="muted" className="max-w-md" align="center">
                  Your changes have been deployed. Click below to view the
                  preview in a new tab.
                </Text>

                {/* Preview URL display */}
                <Inline
                  gap="sm"
                  align="center"
                  className="bg-surface-2 rounded-lg px-4 py-2"
                >
                  <Text
                    as="span"
                    color="secondary"
                    size="xs"
                    className="max-w-[300px] truncate font-mono"
                  >
                    {previewStatus.previewUrl}
                  </Text>
                </Inline>

                {/* Open Preview button */}
                <Stack gap="none" className="mt-2">
                  <Button
                    size="lg"
                    onClick={() =>
                      window.open(previewStatus.previewUrl!, '_blank')
                    }
                  >
                    <ExternalLink className="mr-2 h-5 w-5" />
                    Open Preview
                  </Button>
                </Stack>

                {/* Build info summary */}
                {previewStatus.buildDurationMs && (
                  <Text color="muted" size="sm">
                    Built in {formatDuration(previewStatus.buildDurationMs)}
                  </Text>
                )}
              </Stack>
            </Center>
          )}
      </Card>

      {/* Error message */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Revision limit warning */}
      {isRevisionLimitReached && (
        <Alert variant="destructive">
          <AlertTriangle className="text-error h-5 w-5" />
          <AlertDescription>
            <Text weight="medium">Revision limit reached</Text>
            <Text color="secondary" size="sm">
              You have used all {MAX_REVISIONS} revisions. You must accept the
              changes to proceed.
            </Text>
          </AlertDescription>
        </Alert>
      )}

      {/* Action Buttons */}
      <Inline justify="end" gap="sm">
        <Button
          variant="outline"
          onClick={() => setShowRevisionForm(true)}
          disabled={isRevisionLimitReached}
        >
          <RotateCcw className="mr-2 h-4 w-4" />
          Request Revision
          {!isRevisionLimitReached && (
            <Text as="span" color="muted" className="ml-2">
              ({MAX_REVISIONS - revisionCount} left)
            </Text>
          )}
        </Button>
        <Button onClick={() => setShowAcceptConfirm(true)}>
          <CheckCircle className="mr-2 h-4 w-4" />
          Accept & Continue
        </Button>
      </Inline>
    </Stack>
  );
}
